(() => {
  const GLOBAL_KEY = 'YamuriSeriesComparison';
  const ROOT_SELECTOR = '[data-series-comparison]';
  const existingApi = window[GLOBAL_KEY];

  if (existingApi?.initializeAll && existingApi?.destroyAll) {
    existingApi.initializeAll(document);
    return;
  }

  const instances = new WeakMap();
  const liveSections = new Set();

  const readData = (element, key) => element?.dataset?.[key] || '';

  const normalizeLegacyWarrantyValues = (section) => {
    section.querySelectorAll('.series-comparison__cell-value').forEach((value) => {
      if (value.textContent.trim().toLowerCase() !== '3 years') return;
      const textNode = value.querySelector('p') || value;
      textNode.textContent = '5 years';
    });
  };

  const collectRoots = (container = document) => {
    if (!container) return [];

    const roots = [];
    if (typeof container.matches === 'function' && container.matches(ROOT_SELECTOR)) {
      roots.push(container);
    }
    if (typeof container.querySelectorAll === 'function') {
      container.querySelectorAll(ROOT_SELECTOR).forEach((section) => roots.push(section));
    }
    return Array.from(new Set(roots));
  };

  const restoreAttribute = (element, name, snapshot) => {
    if (!element || !snapshot) return;
    if (snapshot.present) element.setAttribute(name, snapshot.value);
    else element.removeAttribute(name);
  };

  const captureAttribute = (element, name) => ({
    present: Boolean(element?.hasAttribute(name)),
    value: element?.getAttribute(name) || ''
  });

  class SeriesComparison {
    constructor(section, onFatalError) {
      this.section = section;
      normalizeLegacyWarrantyValues(section);
      this.onFatalError = onFatalError;
      this.firstSelect = section.querySelector('[data-series-select="first"]');
      this.secondSelect = section.querySelector('[data-series-select="second"]');
      this.selectors = section.querySelector('[data-series-selectors]');
      this.mobileTable = section.querySelector('[data-series-mobile-table]');
      this.moreButton = section.querySelector('[data-series-more]');
      this.moreWrap = section.querySelector('[data-series-more-wrap]');
      this.extraRows = Array.from(section.querySelectorAll('[data-series-extra-row]'));
      this.mobileRows = [];
      this.productNames = new Map();
      this.productImages = new Map();
      this.availableValues = [];
      this.mutableSlots = [];
      this.snapshot = null;
      this.listenerState = { first: false, second: false, more: false };
      this.destroyed = false;

      this.handleFirstChange = () => this.runSafely(() => this.updateComparison(this.firstSelect));
      this.handleSecondChange = () => this.runSafely(() => this.updateComparison(this.secondSelect));
      this.handleMoreClick = () => this.runSafely(() => {
        const expanded = this.moreButton.getAttribute('aria-expanded') === 'true';
        this.setExpanded(!expanded);
      });
    }

    validate() {
      if (!this.firstSelect || !this.secondSelect || !this.selectors || !this.mobileTable) {
        throw new Error('Required comparison controls are missing.');
      }

      const firstValues = Array.from(this.firstSelect.options).map((option) => option.value);
      const secondValues = Array.from(this.secondSelect.options).map((option) => option.value);
      const uniqueValues = new Set(firstValues);

      if (firstValues.length < 2 || uniqueValues.size !== firstValues.length) {
        throw new Error('At least two unique comparison products are required.');
      }
      if (secondValues.length !== firstValues.length || firstValues.some((value) => !secondValues.includes(value))) {
        throw new Error('Comparison selectors do not contain matching products.');
      }
      if (firstValues.some((value) => !value)) {
        throw new Error('Comparison product keys must not be empty.');
      }

      this.availableValues = firstValues;
      Array.from(this.section.querySelectorAll('[data-series-product-name]')).forEach((template) => {
        this.productNames.set(readData(template, 'seriesProductName'), template);
      });
      Array.from(this.section.querySelectorAll('[data-series-product-image]')).forEach((template) => {
        this.productImages.set(readData(template, 'seriesProductImage'), template);
      });

      const nameSlots = {
        first: this.mobileTable.querySelector('[data-series-name-slot="first"]'),
        second: this.mobileTable.querySelector('[data-series-name-slot="second"]')
      };
      const imageSlots = {
        first: this.mobileTable.querySelector('[data-series-image-slot="first"]'),
        second: this.mobileTable.querySelector('[data-series-image-slot="second"]')
      };

      if (Object.values(nameSlots).some((slot) => !slot) || Object.values(imageSlots).some((slot) => !slot)) {
        throw new Error('Mobile comparison header slots are incomplete.');
      }

      this.availableValues.forEach((productKey) => {
        if (!this.productNames.has(productKey) || !this.productImages.has(productKey)) {
          throw new Error(`Product templates are missing for ${productKey}.`);
        }
      });

      this.mobileRows = Array.from(this.mobileTable.querySelectorAll('tbody tr')).map((row) => {
        const valueSlots = {
          first: row.querySelector('[data-series-value-slot="first"]'),
          second: row.querySelector('[data-series-value-slot="second"]')
        };
        const valueTemplates = new Map();
        Array.from(row.querySelectorAll('[data-series-value]')).forEach((template) => {
          valueTemplates.set(readData(template, 'seriesValue'), template);
        });

        if (!valueSlots.first || !valueSlots.second) {
          throw new Error('A mobile comparison row is missing value slots.');
        }
        this.availableValues.forEach((productKey) => {
          if (!valueTemplates.has(productKey)) {
            throw new Error(`A mobile value template is missing for ${productKey}.`);
          }
        });

        return { valueSlots, valueTemplates };
      });

      if (this.extraRows.length > 0 && (!this.moreButton || !this.moreWrap)) {
        throw new Error('Load More controls are missing for collapsed rows.');
      }

      this.nameSlots = nameSlots;
      this.imageSlots = imageSlots;
      this.mutableSlots = [
        ...Object.values(nameSlots),
        ...Object.values(imageSlots),
        ...this.mobileRows.flatMap((row) => Object.values(row.valueSlots))
      ];
    }

    captureState() {
      const datasetHasReady = Object.prototype.hasOwnProperty.call(this.section.dataset, 'seriesReady');
      this.snapshot = {
        firstValue: this.firstSelect.value,
        secondValue: this.secondSelect.value,
        firstDisabled: Array.from(this.firstSelect.options).map((option) => option.disabled),
        secondDisabled: Array.from(this.secondSelect.options).map((option) => option.disabled),
        slots: this.mutableSlots.map((slot) => ({ slot, html: slot.innerHTML })),
        rowHidden: this.extraRows.map((row) => row.hidden),
        selectorsHidden: this.selectors.hidden,
        mobileHidden: this.mobileTable.hidden,
        moreWrapHidden: this.moreWrap?.hidden,
        moreText: this.moreButton?.textContent || '',
        moreExpanded: captureAttribute(this.moreButton, 'aria-expanded'),
        enhanced: this.section.classList.contains('series-comparison--enhanced'),
        expanded: this.section.classList.contains('series-comparison--expanded'),
        datasetHasReady,
        datasetReady: this.section.dataset.seriesReady
      };
    }

    initialize() {
      this.validate();
      this.captureState();

      try {
        this.setDistinctDefaults();
        this.updateComparison();
        this.attachListeners();
        if (this.extraRows.length > 0) this.setExpanded(false);

        this.section.classList.add('series-comparison--enhanced');
        this.selectors.hidden = false;
        this.mobileTable.hidden = false;
        if (this.moreWrap) this.moreWrap.hidden = false;
        this.section.dataset.seriesReady = 'true';
        this.destroyed = false;
      } catch (error) {
        this.destroy();
        throw error;
      }
    }

    attachListeners() {
      this.firstSelect.addEventListener('change', this.handleFirstChange);
      this.listenerState.first = true;
      this.secondSelect.addEventListener('change', this.handleSecondChange);
      this.listenerState.second = true;
      if (this.moreButton) {
        this.moreButton.addEventListener('click', this.handleMoreClick);
        this.listenerState.more = true;
      }
    }

    setDistinctDefaults() {
      if (!this.availableValues.includes(this.firstSelect.value)) {
        this.firstSelect.value = this.availableValues[0];
      }
      if (!this.availableValues.includes(this.secondSelect.value) || this.secondSelect.value === this.firstSelect.value) {
        this.secondSelect.value = this.availableValues.find((value) => value !== this.firstSelect.value);
      }
    }

    syncDisabledOptions() {
      Array.from(this.firstSelect.options).forEach((option) => {
        option.disabled = option.value === this.secondSelect.value;
      });
      Array.from(this.secondSelect.options).forEach((option) => {
        option.disabled = option.value === this.firstSelect.value;
      });
    }

    renderMobileColumn(slot, productKey) {
      this.nameSlots[slot].innerHTML = this.productNames.get(productKey).innerHTML;
      this.imageSlots[slot].innerHTML = this.productImages.get(productKey).innerHTML;
      this.mobileRows.forEach((row) => {
        row.valueSlots[slot].innerHTML = row.valueTemplates.get(productKey).innerHTML;
      });
    }

    updateComparison(changedSelect) {
      if (this.firstSelect.value === this.secondSelect.value) {
        const otherSelect = changedSelect === this.firstSelect ? this.secondSelect : this.firstSelect;
        const nextValue = this.availableValues.find((value) => value !== changedSelect?.value);
        if (!nextValue) throw new Error('No distinct comparison product is available.');
        otherSelect.value = nextValue;
      }

      this.syncDisabledOptions();
      this.renderMobileColumn('first', this.firstSelect.value);
      this.renderMobileColumn('second', this.secondSelect.value);
    }

    setExpanded(expanded) {
      this.section.classList.toggle('series-comparison--expanded', expanded);
      this.extraRows.forEach((row) => {
        row.hidden = !expanded;
      });
      this.moreButton.setAttribute('aria-expanded', String(expanded));
      this.moreButton.textContent = expanded
        ? readData(this.moreButton, 'lessLabel')
        : readData(this.moreButton, 'moreLabel');
    }

    runSafely(callback) {
      try {
        callback();
      } catch (error) {
        this.onFatalError(this.section, this, error);
      }
    }

    destroy() {
      const safely = (callback) => {
        try { callback(); } catch (_error) { /* Continue restoring the fallback. */ }
      };

      if (this.listenerState.first) {
        safely(() => this.firstSelect.removeEventListener('change', this.handleFirstChange));
        this.listenerState.first = false;
      }
      if (this.listenerState.second) {
        safely(() => this.secondSelect.removeEventListener('change', this.handleSecondChange));
        this.listenerState.second = false;
      }
      if (this.listenerState.more) {
        safely(() => this.moreButton.removeEventListener('click', this.handleMoreClick));
        this.listenerState.more = false;
      }

      const snapshot = this.snapshot;
      if (snapshot) {
        safely(() => { this.firstSelect.value = snapshot.firstValue; });
        safely(() => { this.secondSelect.value = snapshot.secondValue; });
        Array.from(this.firstSelect.options).forEach((option, index) => {
          safely(() => { option.disabled = snapshot.firstDisabled[index]; });
        });
        Array.from(this.secondSelect.options).forEach((option, index) => {
          safely(() => { option.disabled = snapshot.secondDisabled[index]; });
        });
        snapshot.slots.forEach(({ slot, html }) => safely(() => { slot.innerHTML = html; }));
        this.extraRows.forEach((row, index) => safely(() => { row.hidden = snapshot.rowHidden[index]; }));
        safely(() => { this.selectors.hidden = snapshot.selectorsHidden; });
        safely(() => { this.mobileTable.hidden = snapshot.mobileHidden; });
        if (this.moreWrap) safely(() => { this.moreWrap.hidden = snapshot.moreWrapHidden; });
        if (this.moreButton) {
          safely(() => { this.moreButton.textContent = snapshot.moreText; });
          safely(() => restoreAttribute(this.moreButton, 'aria-expanded', snapshot.moreExpanded));
        }
        safely(() => this.section.classList.toggle('series-comparison--enhanced', snapshot.enhanced));
        safely(() => this.section.classList.toggle('series-comparison--expanded', snapshot.expanded));
        safely(() => {
          if (snapshot.datasetHasReady) this.section.dataset.seriesReady = snapshot.datasetReady;
          else delete this.section.dataset.seriesReady;
        });
      } else {
        this.extraRows.forEach((row) => { row.hidden = false; });
        this.section.classList.remove('series-comparison--enhanced', 'series-comparison--expanded');
        delete this.section.dataset.seriesReady;
      }

      this.destroyed = true;
    }
  }

  const reportError = (error) => {
    console.error('[Series comparison] Enhancement failed; using the complete table fallback.', error);
  };

  const handleFatalError = (section, instance, error) => {
    if (instances.get(section) === instance) instances.delete(section);
    liveSections.delete(section);
    instance.destroy();
    reportError(error);
  };

  const initializeOne = (section) => {
    if (instances.has(section)) return instances.get(section);

    let instance;
    try {
      instance = new SeriesComparison(section, handleFatalError);
      instance.initialize();
      instances.set(section, instance);
      liveSections.add(section);
      return instance;
    } catch (error) {
      if (instance && !instance.destroyed) instance.destroy();
      reportError(error);
      return null;
    }
  };

  const initializeAll = (container = document) => {
    collectRoots(container).forEach((section) => initializeOne(section));
  };

  const destroyOne = (section) => {
    const instance = instances.get(section);
    if (!instance) return;
    instance.destroy();
    instances.delete(section);
    liveSections.delete(section);
  };

  const destroyAll = (container = document) => {
    if (container === document) {
      Array.from(liveSections).forEach((section) => destroyOne(section));
      return;
    }
    collectRoots(container).forEach((section) => destroyOne(section));
  };

  const api = { initializeAll, destroyAll };
  window[GLOBAL_KEY] = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document), { once: true });
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
